import Datadog from '../../utils/trackers/Datadog';

const CONTINUE_WATCHING_ALIASES = ['continuaviendo', 'actividadreciente', 'recienvisto'];
const CONTINUE_WATCHING_PENDING_PLAYBACK_KEY = 'continue_watching.pending_playback';

function normalizeValue(value = '') {
  const lowercaseValue = String(value).toLowerCase();
  const normalizedValue = typeof lowercaseValue.normalize === 'function'
    ? lowercaseValue.normalize('NFD')
    : lowercaseValue;

  return normalizedValue
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function getPrimaryError(error = {}) {
  if (error.errors && Array.isArray(error.errors) && error.errors[0]) {
    return error.errors[0];
  }

  if (error.response && error.response.errors && Array.isArray(error.response.errors) && error.response.errors[0]) {
    return error.response.errors[0];
  }

  if (error.completeError && error.completeError.errors && Array.isArray(error.completeError.errors) && error.completeError.errors[0]) {
    return error.completeError.errors[0];
  }

  return error;
}

function getFailureReason(error = {}) {
  const normalizedError = getPrimaryError(error) || {};

  return normalizedError.code || normalizedError.message || error.code || error.message || 'unknown_error';
}

function getHttpStatus(error = {}) {
  if (typeof error.status !== 'undefined' && error.status !== null) {
    return error.status;
  }

  if (error.response && typeof error.response.status !== 'undefined') {
    return error.response.status;
  }

  if (error.completeError && typeof error.completeError.status !== 'undefined') {
    return error.completeError.status;
  }

  return null;
}

function buildRibbonPayload(ribbon = {}) {
  const items = Array.isArray(ribbon.items) ? ribbon.items : [];

  return {
    ribbon_id: ribbon.id || '',
    ribbon_title: ribbon.title || '',
    ribbon_url: ribbon.url || '',
    items_count: items.length,
  };
}

function buildCardPayload(card = {}) {
  return {
    content_id: card.group_id || card.id || '',
    content_title: card.title || '',
    content_type: card.type || '',
    playback_position: card.bookmark || '',
  };
}

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  return window.sessionStorage;
}

export function isContinueWatchingRibbon(ribbon = {}) {
  const candidates = [ribbon.id, ribbon.title, ribbon.url].map(normalizeValue);

  return candidates.some(candidate => CONTINUE_WATCHING_ALIASES.some(alias => candidate.indexOf(alias) !== -1));
}

export function trackContinueWatchingLoadInitiated({ node = '', userStatus = '', hasUserToken = false } = {}) {
  const payload = {
    node,
    user_status: userStatus,
    has_user_token: hasUserToken,
  };

  Datadog.addAction('continue_watching.carousel.load.initiated', payload);

  return payload;
}

export function trackContinueWatchingLoaded({ node = '', userStatus = '', ribbon = null, totalComponents = 0 } = {}) {
  const payload = {
    result: 'success',
    node,
    user_status: userStatus,
    total_components: totalComponents,
    ...buildRibbonPayload(ribbon || {}),
  };

  Datadog.addAction('continue_watching.carousel.loaded', payload);

  return payload;
}

export function trackContinueWatchingLoadFailed({ node = '', userStatus = '', error = {} } = {}) {
  const payload = {
    result: 'error',
    node,
    user_status: userStatus,
    failure_reason: getFailureReason(error),
    http_status: getHttpStatus(error),
  };

  Datadog.addAction('continue_watching.carousel.load_failed', payload);

  return payload;
}

export function trackContinueWatchingRendered({ ribbon = null } = {}) {
  const payload = buildRibbonPayload(ribbon || {});

  Datadog.addAction('continue_watching.carousel.rendered', payload);

  return payload;
}

export function trackContinueWatchingEmpty({ node = '', userStatus = '', totalComponents = 0, ribbon = null } = {}) {
  const payload = {
    node,
    user_status: userStatus,
    total_components: totalComponents,
    ...buildRibbonPayload(ribbon || {}),
  };

  Datadog.addAction('continue_watching.carousel.empty', payload);

  return payload;
}

export function trackContinueWatchingScrolled({ ribbon = null, direction = '', visibleItemsCount = 0, totalItemsCount = 0 } = {}) {
  const payload = {
    direction,
    visible_items_count: visibleItemsCount,
    total_items_count: totalItemsCount,
    ...buildRibbonPayload(ribbon || {}),
  };

  Datadog.addAction('continue_watching.carousel.scrolled', payload);

  return payload;
}

export function trackContinueWatchingDeleteInitiated({ ribbon = null, card = {} } = {}) {
  const payload = {
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.delete.initiated', payload);

  return payload;
}

export function trackContinueWatchingDeleteConfirmed({ ribbon = null, card = {} } = {}) {
  const payload = {
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.delete.confirmed', payload);

  return payload;
}

export function trackContinueWatchingDeleteCancelled({ ribbon = null, card = {} } = {}) {
  const payload = {
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.delete.cancelled', payload);

  return payload;
}

export function trackContinueWatchingDeleteSuccess({ ribbon = null, card = {} } = {}) {
  const payload = {
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.delete.success', payload);

  return payload;
}

export function trackContinueWatchingDeleteFailed({ ribbon = null, card = {} } = {}) {
  const payload = {
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.delete.failed', payload);

  return payload;
}

export function trackContinueWatchingPlaybackResumed({ ribbon = null, card = {} } = {}) {
  const payload = {
    trigger: 'card_click',
    ...buildRibbonPayload(ribbon || {}),
    ...buildCardPayload(card),
  };

  Datadog.addAction('continue_watching.playback.resumed', payload);

  return payload;
}

export function trackContinueWatchingItemClicked(payload = {}) {
  Datadog.addAction('continue_watching.item.clicked', payload);
  return payload;
}

export function setPendingContinueWatchingPlayback({ ribbon = null, card = {} } = {}) {
  const storage = getSessionStorage();

  if (!storage) {
    return false;
  }

  storage.setItem(CONTINUE_WATCHING_PENDING_PLAYBACK_KEY, JSON.stringify({
    ribbon: buildRibbonPayload(ribbon || {}),
    card: buildCardPayload(card || {}),
  }));

  return true;
}

export function consumePendingContinueWatchingPlayback() {
  const storage = getSessionStorage();

  if (!storage) {
    return null;
  }

  const serializedValue = storage.getItem(CONTINUE_WATCHING_PENDING_PLAYBACK_KEY);
  storage.removeItem(CONTINUE_WATCHING_PENDING_PLAYBACK_KEY);

  if (!serializedValue) {
    return null;
  }

  try {
    return JSON.parse(serializedValue);
  } catch (error) {
    return null;
  }
}

export function clearPendingContinueWatchingPlayback() {
  const storage = getSessionStorage();

  if (!storage) {
    return false;
  }

  storage.removeItem(CONTINUE_WATCHING_PENDING_PLAYBACK_KEY);

  return true;
}


// WEBPACK FOOTER //
// ./src/services/datadog/continueWatching.js